/**
 * Typed API client using openapi-fetch.
 *
 * This client is fully typed based on the OpenAPI schema generated from the backend.
 * All API calls are type-checked at compile time.
 * 
 * Authentication is handled via Bearer tokens from WorkOS AuthKit.
 * The token is obtained via getAccessToken() from the useAuth hook.
 */

import createClient from 'openapi-fetch'
import type { paths, components } from './schema'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Request timeout in milliseconds.
 * Set to 30 seconds to ride out a slow or restarting backend
 * while preventing indefinitely hanging requests.
 */
const REQUEST_TIMEOUT_MS = 30_000

/**
 * Creates a fetch function with a timeout.
 * This prevents requests from hanging forever when the backend is waking from suspension.
 */
function createFetchWithTimeout(timeoutMs: number): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    
    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      })
      return response
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timed out after ${timeoutMs}ms`)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Create an authenticated API client with the given access token.
 * Includes a request timeout so a stuck request fails instead of hanging.
 */
export function createAuthenticatedClient(accessToken: string) {
  return createClient<paths>({
    baseUrl: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    fetch: createFetchWithTimeout(REQUEST_TIMEOUT_MS),
  })
}

/**
 * Helper to make an authenticated API request.
 * Use this in React Query hooks where you have access to getAccessToken.
 */
export async function authenticatedFetch<T>(
  accessToken: string,
  fetchFn: (client: ReturnType<typeof createAuthenticatedClient>) => Promise<T>
): Promise<T> {
  const client = createAuthenticatedClient(accessToken)
  return fetchFn(client)
}

// Re-export types for convenience
export type { paths, components }

// Type aliases for common schemas
export type EmailAccount = components['schemas']['InboxRead']
export type Instruction = components['schemas']['InstructionRead']
export type InstructionCreate = components['schemas']['InstructionCreate']
export type InstructionUpdate = components['schemas']['InstructionUpdate']
export type InstructionScope = components['schemas']['InstructionScope']
export type DashboardStats = components['schemas']['DashboardStats']

// Event types
export type EmailEvent = components['schemas']['EmailEventRead']
export type EmailEventDetail = components['schemas']['EmailEventDetailRead']
export type EventAction = components['schemas']['EventActionRead']
export type EventsList = components['schemas']['EventsListResponse']
